import { useState } from "react";
import * as R from "@/lib/rechner";
import { trackSettled } from "@/lib/track";

const de = (v) => (Number(v) || 0).toLocaleString("de-DE", { maximumFractionDigits: 2 });

function Field({ label, value, onChange, suffix }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-sm font-medium">{label}</span>
      <div className="flex items-center rounded-lg border bg-background focus-within:ring-2 focus-within:ring-primary/40">
        <input type="number" inputMode="decimal" value={value}
          onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
          className="w-full bg-transparent px-3 py-2 outline-none" />
        {suffix && <span className="px-3 text-sm text-muted-foreground">{suffix}</span>}
      </div>
    </label>
  );
}

function Choice({ value, onChange, options }) {
  return (
    <div className="inline-flex flex-wrap rounded-lg border p-0.5 bg-muted/40 w-fit">
      {options.map((o) => (
        <button key={o.value} type="button" onClick={() => onChange(o.value)}
          className={`px-3 py-1.5 text-sm rounded-md transition-colors ${value === o.value ? "bg-background shadow-sm font-medium text-foreground" : "text-muted-foreground"}`}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

function Result({ children }) {
  return <div className="rounded-xl bg-secondary/60 p-4 text-lg" data-testid="rechner-ergebnis">{children}</div>;
}

function Shell({ children }) {
  return <div className="rounded-2xl border bg-card p-4 sm:p-6 shadow-sm flex flex-col gap-4">{children}</div>;
}

function track(key) { trackSettled("rechner_benutzt", { calculator: key }); }

function ProzentWidget() {
  const [m, setM] = useState("prozentVon");
  const [a, setA] = useState(19);
  const [b, setB] = useState(200);
  const upd = (fn) => { fn(); track("prozent"); };
  let label1, label2, out;
  if (m === "prozentVon") { label1 = "Prozent"; label2 = "Grundwert"; out = <>{de(a)} % von {de(b)} = <strong>{de(R.prozentVonWert(a, b))}</strong></>; }
  else if (m === "anteil") { label1 = "Teilwert"; label2 = "Grundwert"; out = <>{de(a)} sind <strong>{de(R.anteilProzent(a, b))} %</strong> von {de(b)}</>; }
  else { label1 = "Alter Wert"; label2 = "Neuer Wert"; out = <>Veränderung: <strong>{de(R.veraenderungProzent(a, b))} %</strong></>; }
  return (
    <Shell>
      <Choice value={m} onChange={(v) => upd(() => setM(v))} options={[
        { value: "prozentVon", label: "% von Wert" }, { value: "anteil", label: "Anteil in %" }, { value: "veraenderung", label: "Veränderung" }]} />
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label={label1} value={a} onChange={(v) => upd(() => setA(v))} suffix={m === "prozentVon" ? "%" : ""} />
        <Field label={label2} value={b} onChange={(v) => upd(() => setB(v))} />
      </div>
      <Result>{out}</Result>
    </Shell>
  );
}

function MwstWidget() {
  const [betrag, setBetrag] = useState(100);
  const [satz, setSatz] = useState(19);
  const [richtung, setRichtung] = useState("nettoZuBrutto");
  const r = R.mwst(betrag, satz, richtung);
  const upd = (fn) => { fn(); track("mwst"); };
  return (
    <Shell>
      <div className="flex flex-wrap gap-4">
        <Choice value={richtung} onChange={(v) => upd(() => setRichtung(v))} options={[
          { value: "nettoZuBrutto", label: "Netto → Brutto" }, { value: "bruttoZuNetto", label: "Brutto → Netto" }]} />
        <Choice value={satz} onChange={(v) => upd(() => setSatz(v))} options={[{ value: 19, label: "19 %" }, { value: 7, label: "7 %" }]} />
      </div>
      <Field label={richtung === "bruttoZuNetto" ? "Bruttobetrag" : "Nettobetrag"} value={betrag} onChange={(v) => upd(() => setBetrag(v))} suffix="€" />
      <Result>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div><div className="font-semibold">{de(r.netto)} €</div><div className="text-xs text-muted-foreground">Netto</div></div>
          <div><div className="font-semibold">{de(r.steuer)} €</div><div className="text-xs text-muted-foreground">{satz}% MwSt</div></div>
          <div><div className="font-semibold">{de(r.brutto)} €</div><div className="text-xs text-muted-foreground">Brutto</div></div>
        </div>
      </Result>
    </Shell>
  );
}

function DreisatzWidget() {
  const [a, setA] = useState(2), [b, setB] = useState(10), [c, setC] = useState(5);
  const [m, setM] = useState("proportional");
  const upd = (fn) => { fn(); track("dreisatz"); };
  return (
    <Shell>
      <Choice value={m} onChange={(v) => upd(() => setM(v))} options={[
        { value: "proportional", label: "Proportional" }, { value: "antiproportional", label: "Antiproportional" }]} />
      <div className="grid grid-cols-3 gap-3">
        <Field label="Wenn" value={a} onChange={(v) => upd(() => setA(v))} />
        <Field label="entspricht" value={b} onChange={(v) => upd(() => setB(v))} />
        <Field label="dann" value={c} onChange={(v) => upd(() => setC(v))} />
      </div>
      <Result>{de(c)} entspricht <strong>{de(R.dreisatz(a, b, c, m))}</strong></Result>
    </Shell>
  );
}

function ZinseszinsWidget() {
  const [k, setK] = useState(10000), [r, setR] = useState(0), [p, setP] = useState(5), [n, setN] = useState(10);
  const res = R.zinseszins(k, p, n, r);
  const upd = (fn) => { fn(); track("zinseszins"); };
  return (
    <Shell>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Startkapital" value={k} onChange={(v) => upd(() => setK(v))} suffix="€" />
        <Field label="Sparrate pro Jahr" value={r} onChange={(v) => upd(() => setR(v))} suffix="€" />
        <Field label="Zinssatz" value={p} onChange={(v) => upd(() => setP(v))} suffix="%" />
        <Field label="Laufzeit" value={n} onChange={(v) => upd(() => setN(v))} suffix="Jahre" />
      </div>
      <Result>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div><div className="font-semibold">{de(res.endkapital)} €</div><div className="text-xs text-muted-foreground">Endkapital</div></div>
          <div><div className="font-semibold">{de(res.eingezahlt)} €</div><div className="text-xs text-muted-foreground">Eingezahlt</div></div>
          <div><div className="font-semibold">{de(res.zinsertrag)} €</div><div className="text-xs text-muted-foreground">Zinsertrag</div></div>
        </div>
      </Result>
    </Shell>
  );
}

function StundenlohnWidget() {
  const [richtung, setRichtung] = useState("gehaltZuStundenlohn");
  const [wert, setWert] = useState(3000);
  const [std, setStd] = useState(40);
  const r = R.stundenlohn(wert, std, richtung);
  const upd = (fn) => { fn(); track("stundenlohn"); };
  return (
    <Shell>
      <Choice value={richtung} onChange={(v) => upd(() => setRichtung(v))} options={[
        { value: "gehaltZuStundenlohn", label: "Gehalt → Stundenlohn" }, { value: "stundenlohnZuGehalt", label: "Stundenlohn → Gehalt" }]} />
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label={richtung === "stundenlohnZuGehalt" ? "Stundenlohn" : "Monatsgehalt (brutto)"} value={wert} onChange={(v) => upd(() => setWert(v))} suffix="€" />
        <Field label="Stunden pro Woche" value={std} onChange={(v) => upd(() => setStd(v))} suffix="h" />
      </div>
      <Result>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div><div className="font-semibold">{de(r.stundenlohn)} €</div><div className="text-xs text-muted-foreground">pro Stunde</div></div>
          <div><div className="font-semibold">{de(r.monat)} €</div><div className="text-xs text-muted-foreground">pro Monat</div></div>
          <div><div className="font-semibold">{de(r.jahr)} €</div><div className="text-xs text-muted-foreground">pro Jahr</div></div>
        </div>
      </Result>
    </Shell>
  );
}

function RabattWidget() {
  const [preis, setPreis] = useState(100);
  const [m, setM] = useState("prozent");
  const [wert, setWert] = useState(20);
  const r = R.rabatt(preis, wert, m);
  const upd = (fn) => { fn(); track("rabatt"); };
  return (
    <Shell>
      <Choice value={m} onChange={(v) => upd(() => setM(v))} options={[
        { value: "prozent", label: "Rabatt in %" }, { value: "endpreis", label: "Endpreis bekannt" }]} />
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Originalpreis" value={preis} onChange={(v) => upd(() => setPreis(v))} suffix="€" />
        <Field label={m === "endpreis" ? "Endpreis" : "Rabatt"} value={wert} onChange={(v) => upd(() => setWert(v))} suffix={m === "endpreis" ? "€" : "%"} />
      </div>
      <Result>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div><div className="font-semibold">{de(r.endpreis)} €</div><div className="text-xs text-muted-foreground">Endpreis</div></div>
          <div><div className="font-semibold">{de(r.ersparnis)} €</div><div className="text-xs text-muted-foreground">Ersparnis</div></div>
          <div><div className="font-semibold">{de(r.prozent)} %</div><div className="text-xs text-muted-foreground">Rabatt</div></div>
        </div>
      </Result>
    </Shell>
  );
}

function ArbeitszeitWidget() {
  const [start, setStart] = useState("08:00");
  const [ende, setEnde] = useState("17:00");
  const [pause, setPause] = useState(60);
  const r = R.arbeitszeit(start, ende, pause);
  const upd = (fn) => { fn(); track("arbeitszeit"); };
  const TimeField = ({ label, value, onChange }) => (
    <label className="flex flex-col gap-1">
      <span className="text-sm font-medium">{label}</span>
      <input type="time" value={value} onChange={(e) => onChange(e.target.value)}
        className="rounded-lg border bg-background px-3 py-2 outline-none focus:ring-2 focus:ring-primary/40" />
    </label>
  );
  return (
    <Shell>
      <div className="grid grid-cols-2 gap-4">
        <TimeField label="Beginn" value={start} onChange={(v) => upd(() => setStart(v))} />
        <TimeField label="Ende" value={ende} onChange={(v) => upd(() => setEnde(v))} />
      </div>
      <Field label="Pause" value={pause} onChange={(v) => upd(() => setPause(v))} suffix="Min" />
      <Result>
        Arbeitszeit: <strong>{r.stunden} h {r.rest} min</strong> ({de(r.dezimal)} Std.)
      </Result>
    </Shell>
  );
}

function FlaecheWidget() {
  const [l, setL] = useState(4), [b, setB] = useState(5);
  const r = R.flaeche(l, b);
  const upd = (fn) => { fn(); track("flaeche"); };
  return (
    <Shell>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Länge" value={l} onChange={(v) => upd(() => setL(v))} suffix="m" />
        <Field label="Breite" value={b} onChange={(v) => upd(() => setB(v))} suffix="m" />
      </div>
      <Result>Fläche: <strong>{de(r.flaeche)} m²</strong> · Umfang {de(r.umfang)} m</Result>
    </Shell>
  );
}

function SpritkostenWidget() {
  const [strecke, setStrecke] = useState(300), [verbrauch, setVerbrauch] = useState(7),
    [preis, setPreis] = useState(1.8), [personen, setPersonen] = useState(1);
  const r = R.spritkosten(strecke, verbrauch, preis, personen);
  const upd = (fn) => { fn(); track("spritkosten"); };
  return (
    <Shell>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Strecke" value={strecke} onChange={(v) => upd(() => setStrecke(v))} suffix="km" />
        <Field label="Verbrauch" value={verbrauch} onChange={(v) => upd(() => setVerbrauch(v))} suffix="L/100km" />
        <Field label="Spritpreis" value={preis} onChange={(v) => upd(() => setPreis(v))} suffix="€/L" />
        <Field label="Personen" value={personen} onChange={(v) => upd(() => setPersonen(v))} suffix="Pers." />
      </div>
      <Result>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div><div className="font-semibold">{de(r.kosten)} €</div><div className="text-xs text-muted-foreground">gesamt</div></div>
          <div><div className="font-semibold">{de(r.proPerson)} €</div><div className="text-xs text-muted-foreground">pro Person</div></div>
          <div><div className="font-semibold">{de(r.liter)} L</div><div className="text-xs text-muted-foreground">Sprit</div></div>
        </div>
      </Result>
    </Shell>
  );
}

function MassstabWidget() {
  const [faktor, setFaktor] = useState(87);
  const [richtung, setRichtung] = useState("realZuModell");
  const [wert, setWert] = useState(870);
  const r = R.massstab(faktor, wert, richtung);
  const upd = (fn) => { fn(); track("massstab"); };
  return (
    <Shell>
      <div className="flex items-end gap-3">
        <div className="text-sm font-medium pb-2">Maßstab 1 :</div>
        <Field label="" value={faktor} onChange={(v) => upd(() => setFaktor(v))} />
      </div>
      <Choice value={richtung} onChange={(v) => upd(() => setRichtung(v))} options={[
        { value: "realZuModell", label: "Real → Modell" }, { value: "modellZuReal", label: "Modell → Real" }]} />
      <Field label={richtung === "modellZuReal" ? "Modell-Länge" : "Reale Länge"} value={wert} onChange={(v) => upd(() => setWert(v))} suffix="cm" />
      <Result>{richtung === "modellZuReal" ? "Reale Länge" : "Modell-Länge"}: <strong>{de(r)} cm</strong></Result>
    </Shell>
  );
}

export const WIDGETS = {
  prozent: ProzentWidget,
  mwst: MwstWidget,
  dreisatz: DreisatzWidget,
  zinseszins: ZinseszinsWidget,
  stundenlohn: StundenlohnWidget,
  rabatt: RabattWidget,
  arbeitszeit: ArbeitszeitWidget,
  flaeche: FlaecheWidget,
  spritkosten: SpritkostenWidget,
  massstab: MassstabWidget,
};
